import React, { useState } from 'react';
import { useFetch } from 'app/utils/hooks';
import {
  createContributionCheckInCodeLink,
  getContributionCheckInCodeLink,
  getSongTitle,
} from 'app/store/SongList/selectors';
import { useAppDispatch, useAppSelector } from 'app/store';
import { addAlertAction } from 'app/store/Alerts/slice';
import { isNotFoundError } from 'app/utils/utils';
import { createQrAction } from 'app/store/Mixpanel/slice';
import { QrCheckinComponent } from './QrCheckinComponent';
import { QrCodeResponse } from './types';

interface QrCheckingProps {
  anchorEl: HTMLButtonElement;
  onClose: () => void;
}

export const QrCheckin: React.FC<QrCheckingProps> = ({ anchorEl, onClose }) => {
  const dispatch = useAppDispatch();
  const getQrCodeLink = useAppSelector(getContributionCheckInCodeLink);
  const createQrCodeLink = useAppSelector(createContributionCheckInCodeLink);
  const songTitle = useAppSelector(getSongTitle);
  const fetchQrCode = useFetch<{}, QrCodeResponse>();
  const createQrCode = useFetch<{}, QrCodeResponse>();
  const [error, setError] = useState(false);

  const onError = () => {
    setError(true);
    dispatch(
      addAlertAction({ message: 'Failed to create QR code, please try again' })
    );
  };

  const { callAPI: revokeQrCall } = useFetch<
    any,
    { response: { status: string } }
  >({
    method: 'post',
  });

  const createNewQrCode = async () => {
    try {
      if (createQrCodeLink) {
        await createQrCode.callAPI(createQrCodeLink.href, {});
        dispatch(createQrAction());
      } else {
        onError();
      }
    } catch (e) {
      onError();
    }
  };

  const onFetchQrCode = async () => {
    setError(false);
    try {
      if (getQrCodeLink) {
        await fetchQrCode.callAPI(getQrCodeLink.href);
      } else {
        onError();
      }
    } catch (e: any) {
      if (isNotFoundError(e)) {
        await createNewQrCode();
      } else {
        onError();
      }
    }
  };

  const revokeQrCodeLink = (fetchQrCode.data || createQrCode.data)?.response.qr
    .item._links?.revokeContributionCheckInCode;

  const revokeQr = async () => {
    try {
      if (revokeQrCodeLink) {
        await revokeQrCall(revokeQrCodeLink.href);
        dispatch(addAlertAction({ message: 'QR Code revoked' }));
      } else {
        dispatch(addAlertAction({ message: 'QR Code revoke failed' }));
      }
    } catch (e) {
      dispatch(addAlertAction({ message: 'QR Code revoke failed' }));
    }
  };
  return (
    <QrCheckinComponent
      hasError={error}
      onFetchQrCode={onFetchQrCode}
      isLoading={fetchQrCode.isLoading || createQrCode.isLoading}
      qrCode={
        fetchQrCode.data?.response.qr.item.qrCode ||
        createQrCode.data?.response.qr.item.qrCode
      }
      revokeQr={revokeQr}
      songTitle={songTitle || ''}
      anchorEl={anchorEl}
      onClose={onClose}
    />
  );
};
